import { computed, type Ref } from 'vue'

import { useDayjs, useI18n, useNuxtApp } from '#imports'
import type { JobType } from '~/gql/jobs/queries/findJob/types'
import type { CategoryType } from '~/gql/jobs/queries/getCategories/types'
import { JobTypeEnum, type Location, type LocationOptionEnum } from '~/gql/jobs/schema.generated'

export const jobLocation = (locations: Partial<Location>[]): string => {
  return locations
    .map((location) => location.name)
    .filter((name) => name)
    .join(', ')
}

export type JobDetailsType = {
  startAt: string
  locations: Partial<Location>[]
  locationOptions: LocationOptionEnum[]
}

export function useJobDetails(job: Ref<JobDetailsType | null | undefined>) {
  const { t } = useI18n()
  const dayjs = useDayjs()

  const jobStartAt = computed(() => {
    if (!job.value) return null

    const parsedStartAt = dayjs(job.value.startAt)
    if (parsedStartAt.isSameOrBefore(dayjs(), 'day')) {
      return t('job_details.start_at.immediately')
    } else {
      return parsedStartAt.format('DD.MM.YYYY')
    }
  })

  const jobLocationFull = computed(() => {
    if (!job.value) return null

    const remoteOnly = job.value?.locationOptions && job.value?.locationOptions.length === 1 && job.value?.locationOptions[0] === 'remote'
    if (remoteOnly) {
      return t('job_details.location_options.remote')
    } else {
      const locationOptions = (job.value.locationOptions || []).map((key) => t(`job_details.location_options.${key}`)).join(', ')

      return jobLocation(job.value.locations) + ` (${locationOptions})`
    }
  })

  return { jobStartAt, jobLocationFull }
}

const jobType = (jobTypes: JobType['jobTypes']) => {
  if (jobTypes.includes(JobTypeEnum.FullTime)) {
    return 'FULL_TIME'
  } else if (jobTypes.includes(JobTypeEnum.PartTime)) {
    return 'PART_TIME'
  } else if (jobTypes.includes(JobTypeEnum.Free)) {
    return 'CONTRACTOR'
  } else if (jobTypes.includes(JobTypeEnum.Intern)) {
    return 'INTERN'
  } else if (jobTypes.includes(JobTypeEnum.WorkingStudent)) {
    return 'TEMPORARY'
  } else {
    return 'OTHER'
  }
}

// Used by Google Jobs crawler: https://developers.google.com/search/docs/appearance/structured-data/job-posting
export const jsonld = (job: JobType) => {
  if (job.expired) return null

  return {
    '@context': 'https://schema.org',
    '@type': 'JobPosting',
    title: job.title,
    description: job.description,
    datePosted: job.publishedAt,
    validThrough: job.expiryAt ? new Date(job.expiryAt).toISOString() : undefined,
    employmentType: jobType(job.jobTypes),
    hiringOrganization: {
      '@type': 'Organization',
      name: job.companyName,
      sameAs: job.applicationUrl,
      logo: job.companyLogoThumb,
    },
    jobLocation: {
      '@type': 'Place',
      address: {
        '@type': 'PostalAddress',
        streetAddress: job.street,
        addressLocality: job.city,
        addressRegion: job.country,
        postalCode: job.zipcode,
        addressCountry: job.country,
      },
    },
    baseSalary: {
      '@type': 'MonetaryAmount',
      currency: 'EUR',
      value: {
        '@type': 'QuantitativeValue',
        value: job.salaryFrom,
        minValue: job.salaryFrom,
        maxValue: job.salaryTo,
        unitText: 'YEAR',
      },
    },
    jobStartDate: job.startAt,
    url: import.meta.server ? '' : window.location.href, // TODO: why is this not a url in SSR?
  }
}

export const orderedTranslatedJobCategories = (categories: CategoryType[]) => {
  const { $i18n } = useNuxtApp()

  return categories
    .map((category) => {
      return {
        slug: category.slug,
        name: $i18n.t(`job_list.sidebar.category_filters.${category.slug}`).toString(),
        id: category.id,
      }
    })
    .sort((a, b) => {
      const textA = a.name.toUpperCase()
      const textB = b.name.toUpperCase()
      return textA < textB ? -1 : textA > textB ? 1 : 0
    })
    .map((category) => {
      return { id: category.id, slug: category.slug }
    })
}
